import type { components } from "@shared-core/data/api-schema.generated";
import {
  routeNarrowingFromSchema,
  routeNarrowingToWire,
} from "@shared-core/data/api/catalogue";
import type { SpeedLadderRung } from "@shared-core/data/speed-types";
import {
  RouteNarrowingEditor,
  type RouteChoice,
} from "../catalogue/RouteNarrowingEditor";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

/** The body path the widget owns: the routes and Speeds allowed, as the request sends them. */
const PATH = "routes";

type RouteNarrowingSchema = components["schemas"]["RouteNarrowing"];

/** What the dry run gives the widget to draw from. */
interface FulfilmentRoutesContext {
  scope: "catalogue" | "item";
  routes: { route_code: string; route_label: string; note?: string | null }[];
  speeds: {
    speed_code: string;
    label: string;
    working_days_to_arrival: number;
    needs_a_person: boolean;
  }[];
  /** For an item: what its catalogue already allows, in words. */
  inherited: string | null;
}

function contextOf(
  raw: Record<string, unknown> | null | undefined,
): FulfilmentRoutesContext | null {
  if (!raw || !Array.isArray(raw.routes) || !Array.isArray(raw.speeds))
    return null;
  return raw as unknown as FulfilmentRoutesContext;
}

/**
 * *Set fulfilment routes…* on a catalogue or a catalogue item (catalogue-management-in-one, ruling 2): the editor's own
 * routes and Speeds input. What the choice means for ordering is the dry run's to say, in its note, so the input
 * passes no consequences of its own.
 */
function FulfilmentRoutes(props: CustomWidgetProps) {
  const { body, disabled, errors, hints, setValues } = props;
  const context = contextOf(hints[PATH]?.widget_context);
  if (!context) return null;
  const routes: RouteChoice[] = context.routes.map((r) => ({
    routeCode: r.route_code,
    routeLabel: r.route_label,
    note: r.note ?? null,
  }));
  const speeds: SpeedLadderRung[] = context.speeds.map((s) => ({
    speedCode: s.speed_code,
    label: s.label,
    workingDaysToArrival: s.working_days_to_arrival,
    needsAPerson: s.needs_a_person,
  }));
  const narrowing = routeNarrowingFromSchema(
    body[PATH] as RouteNarrowingSchema | undefined,
  ) ?? { allowedRoutes: [], pinnedRoute: null };
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <RouteNarrowingEditor
        scope={context.scope}
        routes={routes}
        speeds={speeds}
        narrowing={narrowing}
        inherited={context.inherited}
        onChange={(next) => setValues({ [PATH]: routeNarrowingToWire(next) })}
      />
      {errors.map((e) => (
        <p key={`${e.field}:${e.code}`} className="text-sm text-red-600">
          {e.message}
        </p>
      ))}
    </fieldset>
  );
}

export const fulfilmentRoutesWidget: CustomWidget = {
  ownedPaths: [PATH],
  Component: FulfilmentRoutes,
};
